// The backend the scanner talks to. The backends are in the code with their address, token and lists; on start the
// first one that answers of AUTO_ORDER is taken (our GPU recognizer, then TH, then the older platform apibot), unless
// one was picked on the device (ten taps on the search history title, kept in localStorage, or ?backend=<id>).
// A build may override a backend's settings through .env (<PREFIX>_API_URL, _API_TOKEN, _PERSON_LIST_ID,
// _ADD_LIST_ID).
const STORAGE_KEY = 'backend-id';
const AUTO_ORDER = ['gpu', 'th', 'apibot'];
// how long a backend may take to answer the check on start
const CHECK_MS = 4000;

const KNOWN = [
  {
    id: 'th',
    prefix: 'TH',
    KIND: 'platform',
    label: 'NeuroVision TH',
    API_URL: 'https://api-th.neuro-vision.ai',
    API_TOKEN: 'f94a34a0-fae8-11ed-8ce7-23498fdd594e',
    // top20k, imdb-13.01.22, transfermarkt, Kinopoisk5, and the list of people added from the scanner
    PERSON_LIST_ID: '4be87b80-8057-11ec-a5c3-9b55e13323fb,af628720-792a-11ec-b5c6-bed2a27122f5,08e6d540-d73b-11eb-93b8-904dec7abfc1,'
      + '72881150-e526-11eb-a9bc-237e5962fef8,abea7910-d418-11f1-a6bb-8ad7e91a161b',
    ADD_LIST_ID: 'abea7910-d418-11f1-a6bb-8ad7e91a161b',
  },
  {
    id: 'apibot',
    prefix: 'APIBOT',
    KIND: 'apibot',
    label: 'Enface apibot',
    API_URL: 'https://apibot.enface.ai',
    API_TOKEN: '697f5b60-b8ce-11eb-832d-3706db3b4dcd',
    PERSON_LIST_ID: 'af628720-792a-11ec-b5c6-bed2a27122f5,72881150-e526-11eb-a9bc-237e5962fef8,08e6d540-d73b-11eb-93b8-904dec7abfc1,4be87b80-8057-11ec-a5c3-9b55e13323fb,fb9516b0-d3d6-11f1-a509-ce65a932a5d8',
    ADD_LIST_ID: 'fb9516b0-d3d6-11f1-a509-ce65a932a5d8',
    API_SEARCH_PATH: '/action',
    SEARCH_CROP: 'apibot',
  },
  {
    // our recognizer has no lists; the scanner wants one named
    id: 'gpu', prefix: 'GPU', KIND: 'gpu', label: 'NeuroVision GPU', API_URL: 'https://recog.a-kuz.online', PERSON_LIST_ID: 'gallery',
  },
  {
    id: 'celeb', prefix: 'CELEB', KIND: 'platform', label: 'Enface celeb', API_URL: 'https://api.celeb.enface.ai', API_TOKEN: '893502a0-d2f1-11f1-898c-57b4f88721ed', PERSON_LIST_ID: '615ec310-d30a-11f1-87c6-8ee21b51a7e6',
  },
];

const env = key => process.env[key] || '';

export const BACKENDS = KNOWN.map(({ prefix, ...item }) => ({
  API_TOKEN: '',
  ADD_LIST_ID: '',
  API_SEARCH_PATH: '',
  SEARCH_CROP: '',
  APIBOT_NORMALIZED: '',
  ...item,
  ...Object.fromEntries(['API_URL', 'API_TOKEN', 'PERSON_LIST_ID', 'ADD_LIST_ID']
    .filter(key => env(`${prefix}_${key}`))
    .map(key => [key, env(`${prefix}_${key}`)])),
}));

const byId = id => BACKENDS.find(item => item.id === id);
let active = byId('gpu');

const pickedId = () => {
  const asked = new URLSearchParams(window.location.search).get('backend');
  if (asked && byId(asked)) {
    window.localStorage.setItem(STORAGE_KEY, asked);
    return asked;
  }
  const kept = window.localStorage.getItem(STORAGE_KEY);
  return kept && byId(kept) ? kept : null;
};

const timed = (url, init) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CHECK_MS);
  return fetch(url, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
};

// a grey 120 px square, the size of the older platform's face thumbs, for a search that must come back done
const blankFace = () => new Promise(resolve => {
  const canvas = document.createElement('canvas');
  canvas.width = 120;
  canvas.height = 120;
  const context = canvas.getContext('2d');
  context.fillStyle = '#808080';
  context.fillRect(0, 0, 120, 120);
  canvas.toBlob(resolve, 'image/jpeg', 0.8);
});

// Whether the backend answers a real request: the platforms with the token (a refused token or a search that fails
// inside, as apibot's does when its index is down, counts as no), our recognizer its health.
const answers = async item => {
  try {
    if (item.id === 'gpu') {
      const health = await (await timed(`${item.API_URL}/health`)).json();
      return health.status === 'ok';
    }
    if (!item.API_TOKEN) return false;
    const form = new FormData();
    form.append('token', item.API_TOKEN);
    if (item.SEARCH_CROP === 'apibot') {
      form.append('new_person_threshold', 'never');
      form.append('normalized', 'true');
      item.PERSON_LIST_ID.split(',').forEach(list => form.append('person_list_search', list));
      form.append('image', await blankFace(), 'face.jpg');
      const answer = await (await timed(`${item.API_URL}/v1${item.API_SEARCH_PATH}`, { method: 'POST', body: form })).json();
      return answer.status === 'ok';
    }
    // the platform: a search without a picture is refused for the picture, not for the token
    const answer = await (await timed(`${item.API_URL}/v1/face/search`, { method: 'POST', body: form })).json().catch(() => ({}));
    return !/access denied/i.test(JSON.stringify(answer));
  } catch (error) {
    return false;
  }
};

// Settles the backend before the app loads: the one picked on the device, else the first of AUTO_ORDER that answers.
export const detectBackend = async () => {
  const picked = pickedId();
  if (picked) {
    active = byId(picked);
    console.log(`[backend] ${active.id} (picked)`);
    return active;
  }
  for (const id of AUTO_ORDER) {
    // eslint-disable-next-line no-await-in-loop
    if (await answers(byId(id))) {
      active = byId(id);
      console.log(`[backend] ${id} (first of ${AUTO_ORDER.join(', ')} that answers)`);
      return active;
    }
    console.log(`[backend] ${id} does not answer`);
  }
  return active;
};

// The backend in force.
export const backend = () => active;

// Picks a backend ('auto' goes back to the check on start) and loads the app again with it.
export const pickBackend = id => {
  if (id === 'auto') window.localStorage.removeItem(STORAGE_KEY);
  else window.localStorage.setItem(STORAGE_KEY, id);
  const url = new URL(window.location.href);
  url.searchParams.delete('backend');
  window.location.replace(url.toString());
};

export const isPicked = () => !!window.localStorage.getItem(STORAGE_KEY);
